/**
 * Appearance settings applied to the document root: the density (row heights,
 * paddings and font sizes of the data tables and panel lists, as CSS variables
 * the density utilities in globals.css read) and the theme (standard, or the
 * red-shifted night variant selected by data-theme). Pure apart from the root
 * it is handed, so the table and the application are unit tested.
 */
export const DENSITIES = ['comfortable', 'compact', 'spacious'] as const;
export type Density = (typeof DENSITIES)[number];

export const THEMES = ['standard', 'night'] as const;
export type Theme = (typeof THEMES)[number];

export const DENSITY_LABEL: Record<Density, string> = {
  comfortable: 'Comfortable',
  compact: 'Compact',
  spacious: 'Spacious',
};

export const THEME_LABEL: Record<Theme, string> = {
  standard: 'Standard',
  night: 'Night (red, dimmed)',
};

/** The CSS variables a density sets; globals.css maps them to h-row, px-cell-x, text-data, ... */
export const DENSITY_VAR_NAMES = [
  '--density-row',
  '--density-cell-x',
  '--density-cell-y',
  '--density-panel',
  '--density-section',
  '--density-font',
  '--density-font-sm',
] as const;
export type DensityVar = (typeof DENSITY_VAR_NAMES)[number];

/** Comfortable is the default and matches the :root defaults in globals.css. */
export const DENSITY_VARS: Record<Density, Record<DensityVar, string>> = {
  compact: {
    '--density-row': '24px',
    '--density-cell-x': '6px',
    '--density-cell-y': '2px',
    '--density-panel': '8px',
    '--density-section': '12px',
    '--density-font': '11px',
    '--density-font-sm': '10px',
  },
  comfortable: {
    '--density-row': '28px',
    '--density-cell-x': '8px',
    '--density-cell-y': '4px',
    '--density-panel': '12px',
    '--density-section': '16px',
    '--density-font': '12px',
    '--density-font-sm': '10px',
  },
  spacious: {
    '--density-row': '34px',
    '--density-cell-x': '12px',
    '--density-cell-y': '6px',
    '--density-panel': '16px',
    '--density-section': '20px',
    '--density-font': '13px',
    '--density-font-sm': '11px',
  },
};

export const DEFAULT_DENSITY: Density = 'comfortable';
export const DEFAULT_THEME: Theme = 'standard';

export function isDensity(value: unknown): value is Density {
  return typeof value === 'string' && (DENSITIES as readonly string[]).includes(value);
}

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/** The parts of the root element applyAppearance writes (document.documentElement in the app). */
export interface AppearanceRoot {
  dataset: Record<string, string | undefined>;
  style: { setProperty(name: string, value: string): void };
}

/**
 * Writes data-density, data-theme and the density variables on the root.
 * Unknown values (an old or hand-edited persisted state) throw instead of
 * leaving the chrome half styled.
 */
export function applyAppearance(root: AppearanceRoot, appearance: { density: Density; theme: Theme }): void {
  const { density, theme } = appearance;
  if (!isDensity(density)) throw new Error(`applyAppearance: unknown density ${String(density)}`);
  if (!isTheme(theme)) throw new Error(`applyAppearance: unknown theme ${String(theme)}`);
  root.dataset.density = density;
  root.dataset.theme = theme;
  for (const name of DENSITY_VAR_NAMES) root.style.setProperty(name, DENSITY_VARS[density][name]);
}
